import { useMemo, useState } from 'react';
import { cn } from '../cn';

export interface ChartSeries {
  key: string;
  label: string;
  /** Validated categorical hue (see tools: dataviz palette check). */
  color: string;
  points: { t: number; v: number | null }[];
}
export interface TimeSeriesChartProps {
  series: ChartSeries[];
  unit?: string;
  height?: number;
  formatValue?: (v: number) => string;
  formatTime?: (t: number) => string;
  empty?: string;
  className?: string;
  /** Cap the axis at the 97th percentile so one spike (an update download, a reconnect) cannot flatten the rest;
   * clipped points sit on the top edge as open markers and the tooltip/table keep their true values. */
  clipSpikes?: boolean;
}

const defaultTime = (t: number) => new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Line chart for a short time window (one axis, 2 px lines, recessive grid, crosshair + tooltip on hover, legend for
 * 2+ series, table view). Pure SVG, no library: values come from the telemetry history endpoint.
 */
export function TimeSeriesChart({ series, unit = '', height = 160, formatValue = (v) => `${Math.round(v)}${unit}`, formatTime = defaultTime, empty = 'No data yet', className, clipSpikes = false }: TimeSeriesChartProps) {
  const [hover, setHover] = useState<number | null>(null); // index into the time axis
  const [table, setTable] = useState(false);
  const W = 600;
  const H = height;
  const pad = { l: 44, r: 12, t: 10, b: 22 };
  const times = useMemo(() => [...new Set(series.flatMap((s) => s.points.map((p) => p.t)))].sort((a, b) => a - b), [series]);
  const vals = series.flatMap((s) => s.points.map((p) => p.v)).filter((v): v is number => typeof v === 'number');
  if (!times.length || !vals.length) return <div className={cn('flex items-center justify-center font-mono text-sm text-fg4', className)} style={{ height }}>{empty}</div>;
  const t0 = times[0]!;
  const t1 = times[times.length - 1]!;
  const trueMax = Math.max(...vals);
  const sorted = [...vals].sort((a, b) => a - b);
  const p97 = sorted[Math.min(sorted.length - 1, Math.floor(0.97 * (sorted.length - 1)))] ?? trueMax;
  const clipped = clipSpikes && sorted.length >= 8 && trueMax > p97 * 1.5;
  const vmax = Math.max(1, clipped ? p97 * 1.15 : trueMax * 1.1);
  const x = (t: number) => pad.l + ((t - t0) / Math.max(1, t1 - t0)) * (W - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - Math.min(v, vmax) / vmax) * (H - pad.t - pad.b);
  const path = (pts: { t: number; v: number | null }[]) =>
    pts.map((p, i) => (p.v == null ? '' : `${i === 0 || pts[i - 1]!.v == null ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`)).join(' ');
  const ticks = [0, vmax / 2, vmax];
  const xTicks = [t0, (t0 + t1) / 2, t1];
  const hoverT = hover == null ? null : times[hover]!;
  const at = (s: ChartSeries, t: number) => s.points.find((p) => p.t === t)?.v ?? null;
  return (
    <div className={cn('relative min-w-0 max-w-full overflow-hidden', className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        className="block h-auto w-full max-w-full"
        role="img"
        aria-label={series.map((s) => s.label).join(' and ')}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * W;
          let best = 0;
          for (let i = 1; i < times.length; i++) if (Math.abs(x(times[i]!) - px) < Math.abs(x(times[best]!) - px)) best = i;
          setHover(best);
        }}
      >
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth="1" />
            <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--fg4)" fontFamily="var(--font-mono, monospace)">{formatValue(v)}</text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text key={t} x={x(t)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--fg4)" fontFamily="var(--font-mono, monospace)">{formatTime(t)}</text>
        ))}
        {series.map((s) => (
          <path key={s.key} d={path(s.points)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {clipped &&
          series.map((s) =>
            s.points
              .filter((p) => p.v != null && p.v > vmax)
              .map((p) => <circle key={`${s.key}-${p.t}`} cx={x(p.t)} cy={y(vmax)} r="3" fill="var(--panel)" stroke={s.color} strokeWidth="1.5" />),
          )}
        {hoverT != null && (
          <g>
            <line x1={x(hoverT)} x2={x(hoverT)} y1={pad.t} y2={H - pad.b} stroke="var(--fg4)" strokeWidth="1" strokeDasharray="3 3" />
            {series.map((s) => {
              const v = at(s, hoverT);
              return v == null ? null : <circle key={s.key} cx={x(hoverT)} cy={y(v)} r="4" fill={s.color} stroke="var(--panel)" strokeWidth="2" />;
            })}
          </g>
        )}
      </svg>
      {hoverT != null && (
        <div className="pointer-events-none absolute left-2 top-1 rounded-md border border-line bg-panel px-2 py-1 font-mono text-xs text-fg2 shadow">
          <div className="text-fg4">{formatTime(hoverT)}</div>
          {series.map((s) => {
            const v = at(s, hoverT);
            return (
              <div key={s.key} className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-sm" style={{ background: s.color }} />
                {s.label} {v == null ? '—' : formatValue(v)}
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3 font-mono text-xs text-fg3">
        {series.length > 1 &&
          series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        {clipped && <span className="text-fg4">spikes clipped · peak {formatValue(trueMax)}</span>}
        <button type="button" className="ml-auto border-0 bg-transparent p-0 font-mono text-xs text-fg4 underline-offset-2 hover:text-fg hover:underline" onClick={() => setTable((v) => !v)}>
          {table ? 'Hide table' : 'Table'}
        </button>
      </div>
      {table && (
        <table className="mt-2 w-full font-mono text-xs text-fg2">
          <thead>
            <tr className="text-fg4">
              <th className="text-left font-normal">Time</th>
              {series.map((s) => (
                <th key={s.key} className="text-right font-normal">{s.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {times.slice(-12).map((t) => (
              <tr key={t} className="border-t border-line">
                <td className="py-0.5">{formatTime(t)}</td>
                {series.map((s) => {
                  const v = at(s, t);
                  return <td key={s.key} className="py-0.5 text-right">{v == null ? '—' : formatValue(v)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
